import { baseApiUrl, fetchStreamWithAuth, fetchWithAuth } from "@/http/config";
import {
  Channel,
  ChannelHistory,
  ChatSession,
  ServerMessage,
  ServerMessageElement,
  StreamChunk,
} from "@/types/chat";
import { User } from "@/types/user";

interface FetchChannelListPayload {
  limit?: number;
  skip?: number;
  keywords?: string;
}
function fetchChannelList({ limit = 99, skip = 0, keywords }: FetchChannelListPayload): Promise<{
  total: number;
  data_list: Channel[];
}> {
  const searchParams = new URLSearchParams();
  searchParams.set("limit", limit.toString());
  searchParams.set("skip", skip.toString());
  keywords && searchParams.set("channel_name", keywords); // 模糊匹配 Q 或 A

  return fetchWithAuth(`/channel/?${searchParams}`, {
    method: "GET",
  });
}

function fetchTargetFilingChannel(stockCode: string): Promise<{
  total: number;
  data_list: Channel[];
}> {
  const searchParams = new URLSearchParams();
  searchParams.set("source_type", "filings");
  searchParams.set("channel_name", stockCode);

  return fetchWithAuth(`/channel/?${searchParams}`, {
    method: "GET",
  });
}

interface CreateChannelParams {
  name: string;
  islike?: number;
  type?: "filings" | "copilot";
}
function createNewChannel({
  name,
  islike = 0,
  type = "copilot",
}: CreateChannelParams): Promise<Channel> {
  return fetchWithAuth("/channel/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      channel_info: {
        name,
        islike,
        source_type: type,
      },
    }),
  });
}

function deleteChannel(id: number) {
  return fetchWithAuth(`/channel/${id}`, {
    method: "DELETE",
  });
}

function completion(
  channelId: number | string,
  query: string
): Promise<{
  elements: ServerMessageElement[];
  answer: string;
}> {
  return fetchWithAuth("/chat/completion", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      chat_id: channelId,
      query: query,
    }),
  });
}

function completionStream(
  channelId: number | string,
  query: string,
  onChunk: (chunk: StreamChunk) => void = () => {},
  deepAnalysis?: boolean,
  attachments?: string[],
  onFinish?: () => void
) {
  const payload: any = {
    chat_id: channelId,
    query: query,
    deep_analysis_flag: deepAnalysis,
  };

  if (attachments && attachments.length) {
    payload.upload_file_urls = attachments;
  }

  return fetchStreamWithAuth<StreamChunk>(
    "/chat/stream_completion",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
    onChunk,
    onFinish
  );
}

interface GetChannelDetailParams {
  channelId: number;
  skip?: number;
  limit?: number;
}
interface ChannelDetailResponse {
  channel_info: Channel;
  history_total: number;
  chat_history: ChannelHistory[];
}
function fetchChannelDetail({
  channelId,
  skip = 0,
  limit = 10,
}: GetChannelDetailParams): Promise<ChannelDetailResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("channel_id", channelId.toString());
  searchParams.set("skip", skip.toString());
  searchParams.set("limit", limit.toString());
  searchParams.set("require_all", "true");

  return fetchWithAuth<ChannelDetailResponse>(`/channel/detail?${searchParams}`, {
    method: "GET",
  }).then((res) => {
    return {
      ...res,
      chat_history: res.chat_history.reverse(),
    };
  });
}

function shareMessages(
  chatId: number,
  shareList: string[]
): Promise<{
  md5_str: string;
}> {
  return fetchWithAuth("/chat/create_share", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      chat_id: chatId,
      share_list: shareList,
    }),
  });
}

interface ShareMessagesResponse {
  chat_session: Channel;
  share_list: string[]; // example: ["12Q", "45QA"]
  chat_messages: ChannelHistory[];
  user_info: Pick<User, "id" | "username" | "client_name" | "email" | "gender" | "avatar">;
}
function getShareMessages(md5Str: string): Promise<ShareMessagesResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("link", md5Str);
  return fetch(baseApiUrl + `/chat/share_content?${searchParams}`, {
    method: "GET",
  })
    .then((res) => res.json())
    .then((res) => res.result);
}

export {
  fetchChannelList,
  fetchTargetFilingChannel,
  createNewChannel,
  deleteChannel,
  completion,
  completionStream,
  fetchChannelDetail,
  shareMessages,
  getShareMessages,
};
